import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import CreateIcon from '@mui/icons-material/Create';
import DeleteIcon from '@mui/icons-material/Delete';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { AlertColor } from '@mui/lab';
import {
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Theme,
  Tooltip,
} from '@mui/material';
import { green } from '@mui/material/colors';
import makeStyles from '@mui/styles/makeStyles';
import {
  GridRenderCellParams,
  GridRowData,
  GridRowModel,
  GridSortModel,
} from '@mui/x-data-grid';
import clsx from 'clsx';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useMutation } from 'react-query';
import {
  useRecoilState,
  useRecoilStateLoadable,
  useRecoilValueLoadable,
  useSetRecoilState,
} from 'recoil';

import {
  DataTable,
  IColDef,
} from 'src/app/modules/Manage/pages/queues-table/DataTable';

import { useBusinessPermissions } from '../../common/helperFunctions/useBusinessPermissions';
import {
  trackAddCallOutcome,
  trackEditCallOutcome,
  trackSyncCallOutcomes,
  trackSyncCallOutcomesFromOutreach,
} from '../../common/userTracking';
import { calendarState } from '../../recoil/atoms/calendarState';
import { endingDate } from '../../recoil/atoms/endingDate';
import { startingDate } from '../../recoil/atoms/startingDate';
import { Role, user } from '../../recoil/atoms/user';
import {
  makePHPRequest,
  methodNameEnum,
  refetchToggle,
} from '../../recoil/selectors/makePHPRequest';
import { permissionsSelector } from '../../recoil/selectors/permissionsSelector';
import { getApiMutation } from '../../services/rest/getApiMutation';
import {
  endpointEnum,
  makeAPIRequest,
} from '../../services/rest/makeAPIRequest';
import { AddDispositionModal } from '../components/modals/AddDispositionModal';
import { ChangeDispositionModal } from '../components/modals/ChangeDispositionModal';
import { DeleteDispositionModal } from '../components/modals/DeleteDispositionModal';
import { useSnackbarWithStyles } from '../components/notifications/SnackbarProviderWithStyles';
import dataTableScss from '../modules/Manage/pages/queues-table/data-table.module.css';
import {
  EnhancedTableToolbar,
  ToolbarAddDeleteButton,
} from '../modules/Manage/pages/queues-table/EnhancedTableToolbar';

const useStyles = makeStyles((theme: Theme & { custom: any }) => ({
  toolbarButton: {},
  buttonProgress: {
    color: green[500],
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -22,
    marginLeft: -22,
  },
  deleteIconButton: {
    color: theme?.custom?.button?.delete?.main,
  },
  spinner: {
    position: 'fixed !important' as 'fixed',
  },
  audio: {
    transition: 'all 0.5s linear',
    boxShadow: '0px 0px 0px 0px',
    backgroundColor: '#f0f3f4',
    borderRadius: '7px 7px 7px 7px',
    height: 32,
    margin: '10px 0px',
    '&:focus': {
      outline: 0,
    },
  },
  actionButton: {
    whiteSpace: 'nowrap',
    flexDirection: 'column',
    display: 'flex',
    alignItems: 'center',
    '& article': {
      margin: 0,
    },
    '& p': {
      lineHeight: 'normal',
      whiteSpace: 'normal',
      textTransform: 'uppercase',
    },
  },
  userStatusDropdown: {
    marginLeft: '1vw',
    marginTop: '10px',
  },
  syncDispositionsButton: {
    marginRight: 40,
    whiteSpace: 'nowrap',
  },
  syncOutreachButton: {
    right: '25px',
  },
  actionColumn: {
    alignItems: 'center',
    justifyContent: 'center',
  },
}));

export function ManageDispositions() {
  const classes = useStyles();
  const permissionsLoadable = useRecoilValueLoadable(permissionsSelector);
  const { role } = permissionsLoadable.contents;
  const isSuperAdminBoolean = role === Role.SUPERADMIN;
  // const [activeStatus, setActiveStatus] = useState('');
  const [, setCalendarDisplay] = useRecoilState(calendarState);
  const [userAtom, setUserAtom] = useRecoilStateLoadable(user);
  // state for add new Disposition Modal
  const [show, setShow] = useState(false);
  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);
  // state for showing ChangeDispositions Modal
  const [showChange, setShowChange] = useState(false);
  const handleCloseChange = () => setShowChange(false);
  const handleShowChange = () => setShowChange(true);
  const start = useRecoilValueLoadable(startingDate);
  const end = useRecoilValueLoadable(endingDate);

  const refetchToggleForManage = useSetRecoilState(
    refetchToggle(methodNameEnum.GET_USER_DISPOSITION_REPORTING)
  );
  const data = {
    call: methodNameEnum.GET_BUSINESS_DATA,
  };
  const businessData = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: endpointEnum.GET_BUSINESS_DATA,
      payloadJsonString: JSON.stringify(data),
    })
  );
  const disposition = useRecoilValueLoadable(
    makePHPRequest({
      methodName: methodNameEnum.GET_USER_DISPOSITION_REPORTING,
      payloadJsonString: JSON.stringify({
        startdate: start.contents,
        enddate: end.contents,
        timezone:
          businessData?.contents?.response?.data?.result?.[0]?.timezone_php,
      }),
      waitForPayload: !businessData?.contents?.response?.data?.result?.[0]
        ?.timezone_php,
    })
  );
  const { enqueueSnackbarWithStyles } = useSnackbarWithStyles();
  // state props passed to ChangeDispositionModal - line 185 line 52
  const [order, setOrder] = useState('');
  const [dispoId, setDispoId] = useState('');
  const [dispoName, setDispoName] = useState('');
  const [dispoDestinationType, setDispoDestinationType] = useState('none');
  const [dispoDestinationId, setDispoDestinationId] = useState('');
  // state for delete Disposition Modal
  const [showDelete, setShowDelete] = useState(false);
  const handleCloseDelete = () => setShowDelete(false);
  const handleShowDelete = () => setShowDelete(true);

  const [selected, setSelected] = useState<GridRowData[]>([]);
  const [deleteGroupId, setDeleteGroupId] = useState<string | undefined>('');
  // const makeRequest = useMakePHPRequest({
  //   userAtom: userAtom.contents,
  //   setStatus: setDeleteStatus,
  //   setCancelTokenSource: setDeleteCancelTokenSource,
  // });
  const rowsCached = useRef([]);
  rowsCached.current = useMemo(
    () =>
      disposition?.contents?.data?.dispositions?.result
        ? disposition?.contents?.data?.dispositions?.result?.map((o) => ({
            ...o,
            id: o.businessDispositionid,
          }))
        : rowsCached.current,
    [disposition]
  );
  // rowsCached.current = disposition.contents?.data?.dispositions?.result
  //   ? disposition.contents?.data?.dispositions?.result.map((o) => ({
  //       ...o,
  //       id: o.businessDispositionid,
  //     })) ?? []
  //   : rowsCached.current;

  useEffect(() => {
    setCalendarDisplay('none');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  function changeAction({
    order,
    businessDispositionid,
    disposition,
    destinationType,
    destinationId,
  }: GridRowModel) {
    setOrder(order);
    setDispoId(businessDispositionid);
    setDispoName(disposition);
    setDispoDestinationType(
      destinationType && destinationType !== '' ? destinationType : 'none'
    );
    setDispoDestinationId(destinationId);
    handleShowChange();
  }
  const mutation = useMutation(getApiMutation(userAtom.contents, setUserAtom));
  function nameFormatter(params: GridRenderCellParams) {
    return (
      <Tooltip title="Edit">
        <a
          onClick={() => {
            changeAction(params.row);
          }}
        >
          {params.value}
        </a>
      </Tooltip>
    );
  }
  function formatter(params: GridRenderCellParams) {
    // create Actions element here
    return deleteGroupId === params.row.id ? (
      <div>
        <article className={clsx(classes.actionColumn, 'row')}>
          <Tooltip title="Confirm Deletion">
            <IconButton
              aria-label="accept"
              onClick={async (event) => {
                event.preventDefault(); // stop link
                // insert action to delete here
                // const { response } = await makeRequest({
                //   methodName: methodNameEnum.DELETE_DISPOSITION,
                //   payload: {
                // dispositionid: params.row.id,
                //   },
                // });
                const data = {
                  call: methodNameEnum.DELETE_DISPOSITION,
                  apikey: userAtom.contents.apikey,
                  businessid: userAtom.contents.businessid,
                  dispositionid: params.row.id,
                };
                const response = await mutation.mutateAsync({
                  endpoint: endpointEnum.DELETE_DISPOSITION,
                  payload: data,
                });
                if (response?.data?.success) {
                  enqueueSnackbarWithStyles({
                    message: `Successfully Deleted ${params.row.disposition} Disposition`,
                    alertProps: { severity: 'success' },
                  });
                } else {
                  let message:
                    | React.ReactNode
                    | string = `Encountered an error deleting the ${params.row.disposition} Disposition`;
                  let severity: AlertColor = 'error';
                  let cadenceMessage: React.ReactNode | string = '';
                  let webhooksMessage: React.ReactNode | string = '';
                  let powerlistMessage: React.ReactNode | string = '';

                  const errorOnValidate = response?.data?.errorOnValidate;

                  if (
                    errorOnValidate?.errorType?.includes(
                      'FK_CADENCE__BUSINESS_DISPOSITIONS'
                    ) &&
                    errorOnValidate?.entities
                  ) {
                    const cadences = errorOnValidate.entities?.cadences;
                    cadenceMessage = (
                      <div>
                        Cannot remove disposition since it's used in the
                        following Cadences:
                        <ul>
                          {cadences.map((cadence) => (
                            <li key={cadence.cadenceId}>
                              <a
                                href={`/manage/cadences/${cadence.cadenceId}/edit`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  color: 'white',
                                  textDecoration: 'underline',
                                }}
                              >
                                {cadence.cadenceName}
                              </a>
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                    severity = 'warning';
                  }

                  if (
                    errorOnValidate?.errorType?.includes(
                      'FK_POWERLISTS__BUSINESS_DISPOSITIONS'
                    ) &&
                    errorOnValidate?.entities?.powerlists
                  ) {
                    const powerlists = errorOnValidate.entities?.powerlists;
                    powerlistMessage = (
                      <div>
                        Cannot remove disposition since it's used in the
                        following Powerlists:
                        <ul>
                          {powerlists.map((powerlist) => (
                            <li key={powerlist.powerlistId}>
                              <a
                                href={`/manage/powerlists/${powerlist.powerlistId}/edit`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  color: 'white',
                                  textDecoration: 'underline',
                                }}
                              >
                                {powerlist.name}
                              </a>
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                    severity = 'warning';
                  }

                  if (
                    errorOnValidate?.errorType?.includes(
                      'FK_WEBHOOKS__BUSINESS_DISPOSITIONS'
                    ) &&
                    errorOnValidate?.entities
                  ) {
                    const webhooks = errorOnValidate.entities?.webhooks;
                    webhooksMessage = (
                      <div>
                        Cannot remove disposition since it's used in the
                        following Webhooks:
                        <ul>
                          {webhooks.map((webhook) => (
                            <li key={webhook.webhookid}>
                              <a
                                href={`/manage/webhooks/${webhook.webhookid}/edit`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  color: 'white',
                                  textDecoration: 'underline',
                                }}
                              >
                                {webhook.name}
                              </a>
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                    severity = 'warning';
                  }

                  if (
                    cadenceMessage != '' ||
                    powerlistMessage != '' ||
                    webhooksMessage != ''
                  ) {
                    message = (
                      <div>
                        {cadenceMessage} {powerlistMessage} {webhooksMessage}
                      </div>
                    );
                  }

                  enqueueSnackbarWithStyles({
                    message,
                    alertProps: { severity },
                  });
                }
                refetchToggleForManage({});
              }}
              disabled={mutation.isLoading}
              size="large"
            >
              <CheckIcon color={mutation.isLoading ? 'disabled' : 'primary'} />
              {mutation.isLoading && (
                <CircularProgress
                  size={43}
                  className={classes.buttonProgress}
                />
              )}
            </IconButton>
          </Tooltip>
          <Tooltip title="Cancel">
            <IconButton
              aria-label="cancel"
              onClick={(event) => {
                event.preventDefault(); // stop link
                // reset groupid hide confirm / cancel deletion
                setDeleteGroupId(undefined);
              }}
              disabled={mutation.isLoading}
              size="large"
            >
              <ClearIcon
                color="disabled"
                className={clsx({
                  [classes.deleteIconButton]: !mutation.isLoading, // || deleteCancelTokenSource,
                })}
              />
            </IconButton>
          </Tooltip>
        </article>
      </div>
    ) : (
      <div className={clsx(classes.actionColumn, 'row')}>
        <Tooltip title="Edit">
          <IconButton
            name="edit"
            onClick={() => {
              changeAction(params.row);
              trackEditCallOutcome();
            }}
            size="large"
          >
            <CreateIcon color="primary" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Delete">
          <IconButton
            aria-label="delete"
            onClick={() => setDeleteGroupId(params.row.id)}
            size="large"
          >
            <DeleteIcon className={classes.deleteIconButton} />
          </IconButton>
        </Tooltip>
      </div>
    );
  }
  const readOnly = useBusinessPermissions().accountReadOnly;
  const columns = [
    {
      field: 'order',
      headerName: 'Order',
      // dataAlign: 'center',
      headerAlign: 'left',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
    },
    {
      field: 'disposition',
      headerName: 'Call Outcome',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
      renderCell: nameFormatter,
      // width: 400,
    },
    {
      field: 'id',
      headerName: 'Actions',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
      hide: readOnly,
      renderCell: formatter,
      disableExport: true,
      enableSorting: false,
      headerAlign: 'center',
      align: 'center',
      width: 120,
    },
  ] /* satisfies */ as IColDef[];

  const handleUpdateFromHubSpot = async () => {
    try {
      const response = await mutation.mutateAsync({
        endpoint: `${endpointEnum.UPDATE_DISPOSITIONS_HUBSPOT}/${userAtom.contents?.businessid}`,
        payload: {
          userid: userAtom.contents?.userid,
        },
      });
      refetchToggleForManage({});
      if (response?.data?.msg || response?.data?.message) {
        enqueueSnackbarWithStyles({
          // message: `No HubSpot CRM available`,
          message: `Error syncing call outcomes: ${
            response?.data?.msg ?? response?.data?.message
          }`,
          alertProps: { severity: 'error' },
        });
      } else {
        enqueueSnackbarWithStyles({
          message: `Call outcomes successfully synced`,
          alertProps: { severity: 'success' },
        });
      }
    } catch (e) {
      enqueueSnackbarWithStyles({
        message: `There was an error syncing call outcomes`,
        alertProps: { severity: 'error' },
      });
    }
  };
  const handleUpdateFromOutreach = async () => {
    try {
      const response = await mutation.mutateAsync({
        endpoint: 'outreach/outreachActionHandler',
        domain: `${process.env.VITE_APP_CDK_DOMAIN}`,
        stage: 'crm',
        payload: {
          userid: userAtom.contents?.userid,
          action: 'syncOutcomes',
        },
      });
      refetchToggleForManage({});
      if (response.status !== 200) {
        enqueueSnackbarWithStyles({
          message: `Error syncing call outcomes: ${
            response?.data?.msg ?? response?.data?.message
          }`,
          alertProps: { severity: 'error' },
        });
      } else {
        enqueueSnackbarWithStyles({
          message: `Call outcomes successfully synced`,
          alertProps: { severity: 'success' },
        });
      }
    } catch (e) {
      enqueueSnackbarWithStyles({
        message: `There was an error syncing call outcomes`,
        alertProps: { severity: 'error' },
      });
    }
  };

  const ToolbarComponent = useCallback(
    (props) => (
      <EnhancedTableToolbar
        {...props}
        dataType="Call Outcomes"
        numSelected={selected.length}
        centerComponent={
          <Button
            className={classes.toolbarButton}
            variant="outlined"
            color="primary"
            endIcon={<HelpOutlineIcon />}
            target="_blank"
            href="https://kixie.zendesk.com/hc/en-us/articles/360013241573-How-to-Use-the-Call-Disposition-Call-Outcome-Feature-with-Automations"
            rel="noopener noreferrer"
          >
            How to use Call Outcomes
          </Button>
        }
        rightComponent={
          <>
            {
              isSuperAdminBoolean && (
                <>
                  <Button
                    className={
                      `${classes.toolbarButton} ${classes.syncDispositionsButton}` +
                      ' superStyle'
                    }
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      trackSyncCallOutcomes();
                      handleUpdateFromHubSpot();
                    }}
                  >
                    Sync from HubSpot
                  </Button>
                  <Button
                    className={
                      `${classes.toolbarButton} ${classes.syncDispositionsButton} ${classes.syncOutreachButton}` +
                      ' superStyle'
                    }
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      trackSyncCallOutcomesFromOutreach();
                      handleUpdateFromOutreach();
                    }}
                  >
                    Sync from Outreach
                  </Button>

                  {/* <FormControl
                    className={classes.userStatusDropdown}
                    size="small"
                    variant="outlined"
                  >
                    <InputLabel>Sync Call Outcomes</InputLabel>
                    <Select
                      // value={activeStatus}
                      value={[]}
                      onChange={handleUpdateFromHubSpot}
                      label="Sync Call Outcomes"
                      style={{ width: '150px', margin: '0 5px' }}
                    >
                      <MenuItem value="hubspot">Update From HubSpot</MenuItem>
                    </Select>
                  </FormControl> */}
                </>
              )
              // Might switch from button to dropdown when we support disposition syncing from more CRMs
            }
            <ToolbarAddDeleteButton
              numSelected={selected.length}
              onClickForAdd={() => {
                trackAddCallOutcome();
                handleShow();
              }}
              onClickForDelete={handleShowDelete}
              isLoading={props.isLoading}
              // style={{ padding: '30px' }}
            />
          </>
        }
      />
    ),
    [
      selected,
      classes.toolbarButton,
      classes.syncDispositionsButton,
      classes.syncOutreachButton,
      isSuperAdminBoolean,
      handleUpdateFromHubSpot,
      handleUpdateFromOutreach,
    ]
  );
  const [sortModel, setGridSortModel] = useState<GridSortModel>([
    { field: 'order', sort: 'asc' },
  ]);
  return (
    <Paper square={true} elevation={0}>
      <DataTable
        enableSelectAll={true}
        setSelected={setSelected}
        rows={rowsCached?.current ?? []}
        columns={columns}
        loading={disposition.state !== 'hasValue'}
        ToolbarComponent={ToolbarComponent}
        sortModel={sortModel}
        setGridSortModel={setGridSortModel}
        selected={selected}
      />
      <AddDispositionModal handleClose={handleClose} show={show} />
      <ChangeDispositionModal
        handleClose={handleCloseChange}
        show={showChange}
        order={order}
        dispoId={dispoId}
        dispoName={dispoName}
        dispoDestinationType={dispoDestinationType}
        dispoDestinationId={dispoDestinationId}
      />
      <DeleteDispositionModal
        handleClose={handleCloseDelete}
        mutation={mutation}
        show={showDelete}
        userAtom={userAtom}
        setUserAtom={setUserAtom}
        selected={selected}
        refetchToggle={refetchToggleForManage}
        enqueueSnackbarWithStyles={enqueueSnackbarWithStyles}
      />
    </Paper>
  );
}
